import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    X,
    Github,
    GitBranch,
    Search,
    Lock,
    Globe,
    Loader2,
    ArrowLeft,
    Download,
    ChevronDown,
    Monitor,
    Smartphone,
    ChevronRight,
} from 'lucide-react';
import { useGitHub, GitHubRepository } from '@/contexts/GithubContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { usePlanFeatures, getUpgradeCtaLabel } from '@/hooks/usePlanFeatures';
import { useNavigate } from 'react-router-dom';
import { resolveBranchSelection } from '@/utils/githubRepoUrl';

interface GithubImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImported?: (projectId: string, projectUrl: string) => void;
    /** Set when opened from the paid Migration support flow, which imports before payment and is exempt from the free-plan import gate */
    viaMigrationService?: boolean;
}

type Step = 'type-select' | 'pick-repo' | 'configure';
type ProjectType = 'fullstack' | 'mobile';

const PROJECT_TYPES: { id: ProjectType; label: string; sub: string; icon: React.ReactNode; features: string[]; hidden?: boolean }[] = [
    {
        id: 'fullstack',
        label: 'Web App',
        sub: 'React + Express',
        icon: <Monitor className="w-6 h-6" />,
        features: ['React frontend', 'Express backend', 'Runs in browser'],
    },
    {
        id: 'mobile',
        label: 'Mobile App',
        sub: 'React Native / Expo',
        icon: <Smartphone className="w-6 h-6" />,
        features: ['iOS & Android', 'Expo framework', 'Native components'],
        // Mobile import is paused while we fix container startup issues — flip to false to bring it back.
        hidden: true,
    },
];

export const GithubImportModal: React.FC<GithubImportModalProps> = ({
    isOpen,
    onClose,
    onImported,
    viaMigrationService,
}) => {
    const { isConnected, listRepositories, listBranchesWithDefault, importProject, initiateConnection } = useGitHub();
    const { activeWorkspace } = useWorkspace();
    const { isRestricted, isExpired } = usePlanFeatures();
    const navigate = useNavigate();

    // Migration support imports before it charges, so it stays exempt from this check.
    // Everyone else finds out immediately on open instead of after configuring the whole import.
    const blockedByPlan = !viaMigrationService && isRestricted('github_connect');

    const [step, setStep] = useState<Step>('type-select');
    const [selectedType, setSelectedType] = useState<ProjectType | null>(null);
    const [repos, setRepos] = useState<GitHubRepository[]>([]);
    const [isLoadingRepos, setIsLoadingRepos] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(null);
    const [projectName, setProjectName] = useState('');
    const [branch, setBranch] = useState('');
    const [branches, setBranches] = useState<string[]>([]);
    const [isLoadingBranches, setIsLoadingBranches] = useState(false);
    const branchRequestRef = useRef<string | null>(null);

    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadRepos = useCallback(async () => {
        setIsLoadingRepos(true);
        try {
            const result = await listRepositories({ perPage: 100, sort: 'updated' });
            setRepos(result?.repositories ?? []);
        } finally {
            setIsLoadingRepos(false);
        }
    }, [listRepositories]);

    useEffect(() => {
        if (isOpen && isConnected && step === 'pick-repo') loadRepos();
    }, [isOpen, isConnected, step, loadRepos]);

    useEffect(() => {
        if (!isOpen) {
            setStep('type-select');
            setSelectedType(null);
            setSelectedRepo(null);
            setProjectName('');
            setBranch('');
            setBranches([]);
            branchRequestRef.current = null;
            setError(null);
            setSearchQuery('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleSelectType = (type: ProjectType) => {
        setSelectedType(type);
        setStep('pick-repo');
        if (isConnected) loadRepos();
    };

    const handleSelectRepo = async (repo: GitHubRepository) => {
        setSelectedRepo(repo);
        setProjectName(repo.name);
        setBranch(repo.defaultBranch || 'main');
        setBranches([]);
        setError(null);
        setStep('configure');

        branchRequestRef.current = repo.fullName;
        setIsLoadingBranches(true);
        try {
            const { branches: list, defaultBranch } = await listBranchesWithDefault(repo.fullName);
            // The user may have gone back and picked another repo while this one was loading
            if (branchRequestRef.current !== repo.fullName) return;
            if (list.length > 0) {
                const resolved = resolveBranchSelection(list, defaultBranch || repo.defaultBranch);
                setBranches(resolved.branches);
                setBranch(resolved.selected);
            }
        } finally {
            if (branchRequestRef.current === repo.fullName) setIsLoadingBranches(false);
        }
    };

    const handleImport = async () => {
        if (!selectedRepo || !projectName.trim() || !selectedType) return;
        setIsImporting(true);
        setError(null);

        const workspaceIdNum = activeWorkspace?.id ? parseInt(String(activeWorkspace.id), 10) : undefined;
        const result = await importProject({
            repositoryFullName: selectedRepo.fullName,
            projectName: projectName.trim(),
            branch,
            workspaceId: workspaceIdNum && !isNaN(workspaceIdNum) ? workspaceIdNum : undefined,
            projectType: selectedType,
            viaMigrationService,
        });

        setIsImporting(false);

        if (result.success && result.data) {
            const { projectId, projectUrl } = result.data;
            onImported?.(projectId, projectUrl ?? '');
            onClose();
            navigate(`/projects/${projectId}`, { state: { isGitHubImport: true } });
        } else {
            setError(result.error || 'Import failed. Please try again.');
        }
    };

    const filteredRepos = repos.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.fullName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleBack = () => {
        if (step === 'configure') setStep('pick-repo');
        else if (step === 'pick-repo') setStep('type-select');
    };

    const headerTitle = step === 'type-select'
        ? 'Import from GitHub'
        : step === 'pick-repo'
        ? 'Select Repository'
        : 'Configure Import';

    const headerSub = blockedByPlan
        ? 'Upgrade to import from GitHub'
        : step === 'type-select'
        ? 'Choose your project type to get started'
        : step === 'pick-repo'
        ? PROJECT_TYPES.find(t => t.id === selectedType)?.label ?? ''
        : selectedRepo?.fullName ?? '';

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10020] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-lg flex flex-col max-h-[90vh]">
                <div className="relative bg-white rounded-2xl shadow-2xl border border-line overflow-hidden flex flex-col max-h-[90vh] dark:bg-surface dark:border-white/10">
                    {/* Accent bar */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-sunset flex-shrink-0" />

                    {/* Header */}
                    <div className="relative px-5 sm:px-6 pt-6 pb-4 flex-shrink-0">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-soft transition-colors text-muted-foreground hover:text-foreground dark:hover:bg-white/10 dark:text-gray-400 dark:hover:text-white"
                        >
                            <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>

                        <div className="flex items-center gap-3">
                            {step !== 'type-select' && (
                                <button
                                    onClick={handleBack}
                                    className="p-1.5 rounded-lg hover:bg-soft transition-colors text-muted-foreground hover:text-foreground flex-shrink-0 dark:hover:bg-white/10 dark:text-gray-400 dark:hover:text-white"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                </button>
                            )}
                            <div className="p-2.5 sm:p-3 rounded-xl bg-ink-blue-900 text-white flex-shrink-0">
                                <Github className="w-5 h-5 sm:w-6 sm:h-6" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-lg sm:text-xl font-bold text-foreground tracking-tight">
                                    {headerTitle}
                                </h2>
                                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
                                    {headerSub}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-line flex-shrink-0 dark:border-white/10" />

                    {/* Scrollable content */}
                    <div className="overflow-y-auto flex-1 px-5 sm:px-6 py-4">
                        {blockedByPlan ? (
                            <div className="text-center py-10">
                                <div className="p-4 rounded-2xl bg-soft inline-flex mb-4 dark:bg-white/5">
                                    <Lock className="w-8 h-8 text-plum" />
                                </div>
                                <p className="text-sm text-muted-foreground mb-5">
                                    GitHub import requires a paid plan. Upgrade to continue.
                                </p>
                                <button
                                    onClick={() => { onClose(); navigate('/billing/plans'); }}
                                    className="w-full py-3 rounded-xl font-medium bg-gradient-sunset text-white flex items-center justify-center gap-2 hover:shadow-soft transition-all"
                                >
                                    {getUpgradeCtaLabel(isExpired)}
                                </button>
                            </div>
                        ) : !isConnected ? (
                            <div className="text-center py-10">
                                <div className="p-4 rounded-2xl bg-soft inline-flex mb-4 dark:bg-white/5">
                                    <Github className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground mb-5">
                                    Connect your GitHub account to import repositories.
                                </p>
                                <button
                                    onClick={initiateConnection}
                                    className="w-full py-3 rounded-xl font-medium bg-gradient-sunset text-white flex items-center justify-center gap-2 hover:shadow-soft transition-all"
                                >
                                    <Github className="w-4 h-4" />
                                    Connect GitHub Account
                                </button>
                            </div>
                        ) : step === 'type-select' ? (
                            /* ── Type selection tiles ── */
                            <div className="space-y-3">
                                <p className="text-xs text-muted-foreground mb-1">
                                    This determines the workspace and preview environment for your project.
                                </p>
                                {PROJECT_TYPES.filter(type => !type.hidden).map(type => (
                                    <button
                                        key={type.id}
                                        onClick={() => handleSelectType(type.id)}
                                        className="w-full text-left flex items-center gap-4 px-4 py-4 rounded-xl border-2 border-line hover:border-ink-blue-900/30 hover:bg-soft transition-all group dark:border-white/10 dark:hover:border-white/30 dark:hover:bg-white/5"
                                    >
                                        <div className="p-2.5 rounded-xl bg-soft text-muted-foreground group-hover:bg-ink-blue-900 group-hover:text-white transition-all flex-shrink-0 dark:bg-white/10 dark:text-gray-400 dark:group-hover:bg-white/20 dark:group-hover:text-white">
                                            {type.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-foreground">{type.label}</p>
                                                <span className="text-xs text-muted-foreground font-normal">{type.sub}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                {type.features.map(f => (
                                                    <span key={f} className="text-xs text-muted-foreground bg-soft px-2 py-0.5 rounded-full dark:text-gray-400 dark:bg-white/10">
                                                        {f}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0 transition-colors dark:text-gray-500 dark:group-hover:text-white" />
                                    </button>
                                ))}
                            </div>
                        ) : step === 'pick-repo' ? (
                            /* ── Repo picker ── */
                            <div className="space-y-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search repositories..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2.5 bg-soft border border-line rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-plum/50 transition-colors dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder-gray-500"
                                    />
                                </div>

                                {isLoadingRepos ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : filteredRepos.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-sm text-muted-foreground">
                                            {searchQuery ? 'No matching repositories.' : 'No repositories found.'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-0.5">
                                        {filteredRepos.map(repo => (
                                            <button
                                                key={repo.id}
                                                onClick={() => handleSelectRepo(repo)}
                                                className="w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-soft border border-transparent hover:border-input transition-all group dark:hover:bg-white/5 dark:hover:border-white/10"
                                            >
                                                <div className="mt-0.5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 dark:text-gray-500 dark:group-hover:text-gray-300">
                                                    {repo.private ? <Lock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-foreground truncate">{repo.fullName}</p>
                                                    {repo.description && (
                                                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{repo.description}</p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                        {repo.language && (
                                                            <span className="text-xs text-muted-foreground">{repo.language}</span>
                                                        )}
                                                        {repo.defaultBranch && (
                                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                <GitBranch className="w-3 h-3" />
                                                                {repo.defaultBranch}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* ── Configure step ── */
                            <div className="space-y-4">
                                {/* Selected repo */}
                                {selectedRepo && (
                                    <div className="flex items-center gap-2.5 px-3.5 py-3 bg-soft rounded-xl border border-line dark:bg-white/5 dark:border-white/10">
                                        {selectedRepo.private
                                            ? <Lock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                            : <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                                        <span className="text-sm font-medium text-foreground truncate">{selectedRepo.fullName}</span>
                                    </div>
                                )}

                                {/* Project name */}
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                                        Project Name
                                    </label>
                                    <input
                                        type="text"
                                        value={projectName}
                                        onChange={e => setProjectName(e.target.value)}
                                        className="w-full px-3.5 py-2.5 bg-white border border-line rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-plum/50 transition-colors dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder-gray-500"
                                        placeholder="My Project"
                                    />
                                </div>

                                {/* Branch selector */}
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                                        Branch
                                    </label>
                                    {isLoadingBranches ? (
                                        <div className="flex items-center gap-2 px-3.5 py-2.5 bg-soft border border-line rounded-xl dark:bg-white/5 dark:border-white/10">
                                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">Loading branches…</span>
                                        </div>
                                    ) : branches.length > 0 ? (
                                        <div className="relative">
                                            <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                            <select
                                                value={branch}
                                                onChange={e => setBranch(e.target.value)}
                                                className="w-full pl-9 pr-8 py-2.5 bg-white border border-line rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-plum/50 transition-colors appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                                            >
                                                {branches.map(b => (
                                                    <option key={b} value={b}>{b}</option>
                                                ))}
                                            </select>
                                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <input
                                                type="text"
                                                value={branch}
                                                onChange={e => setBranch(e.target.value)}
                                                className="w-full pl-9 pr-3.5 py-2.5 bg-white border border-line rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-plum/50 transition-colors dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder-gray-500"
                                                placeholder="main"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Error */}
                                {error && (
                                    <div className="px-3.5 py-3 bg-red-50 border border-red-200 rounded-xl dark:bg-red-950/30 dark:border-red-900/40">
                                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                                    </div>
                                )}

                                {/* Info */}
                                <div className="bg-soft rounded-xl p-3.5 border border-line dark:bg-white/5 dark:border-white/10">
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        A new project will be created and you'll be taken there immediately.
                                        Cloning your repository happens in the background — a message will appear in the chat when it's done.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer — configure step only */}
                    {isConnected && step === 'configure' && (
                        <>
                            <div className="border-t border-line flex-shrink-0 dark:border-white/10" />
                            <div className="flex gap-3 px-5 sm:px-6 py-4 flex-shrink-0">
                                <button
                                    onClick={onClose}
                                    disabled={isImporting}
                                    className="flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border border-line bg-white hover:bg-soft text-muted-foreground transition-colors disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:text-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleImport}
                                    disabled={isImporting || !projectName.trim() || !branch || isLoadingBranches}
                                    className="flex-1 py-2.5 rounded-xl font-medium bg-gradient-sunset text-white flex items-center justify-center gap-2 hover:shadow-soft transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    {isImporting
                                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</>
                                        : <><Download className="w-4 h-4" /> Import Project</>
                                    }
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default GithubImportModal;
